import { useEffect, useMemo, useRef } from 'react';
import { useAppNavigate } from './impersonation';
import { webRequest } from './api';
import { CoachScreen, type CoachClient } from '../../shared/src/screens/CoachScreen';
import { MAIN_TOPIC } from '../../shared/src/utils/coachTopic';
import { httpCoachClient } from '../../shared/src/utils/httpClients';
import type { CoachSource, CoachTopic } from '../../shared/src/types/coach';

/** The coach over fetch; only the main chat carries saved notes and check-ins. */
export const webCoachClient = (
  token: string,
  topic: CoachTopic,
  openSource?: (source: CoachSource) => void
): CoachClient => httpCoachClient(webRequest(token), topic, openSource);

/**
 * Opening the full conversation behind a source is a staff tool. The opener stays the same across
 * navigation, so chats built on it are not reloaded when the address changes.
 */
export function useSourceOpener(canOpenSources: boolean) {
  const navigate = useAppNavigate();
  const latest = useRef(navigate);
  useEffect(() => {
    latest.current = navigate;
  }, [navigate]);
  return useMemo(
    () =>
      canOpenSources
        ? (source: CoachSource) =>
            latest.current('/app/conversations?thread=' + encodeURIComponent(source.edgeId))
        : undefined,
    [canOpenSources]
  );
}

export function WorkspaceCoach({
  token,
  hasMessages = true,
  canOpenSources = false,
}: {
  token: string;
  hasMessages?: boolean;
  canOpenSources?: boolean;
}) {
  const navigate = useAppNavigate();
  const openSource = useSourceOpener(canOpenSources);
  const client = useMemo(() => webCoachClient(token, MAIN_TOPIC, openSource), [token, openSource]);
  return (
    <div className="workspace-coach">
      <CoachScreen
        key={token}
        client={client}
        hasMessages={hasMessages}
        onConnect={() => navigate('/app/settings')}
      />
    </div>
  );
}
